/**
 * Paso 3 del asistente: qué dato va en cada hueco {{N}} de la plantilla.
 *
 * Por variable, dos fuentes: «Un dato del cliente» (un campo de la base de Activa) o «Siempre el
 * mismo texto». Con un dato del cliente hay que decidir qué pasa si a alguien le falta: usar un
 * texto en su lugar, no mandarle o dejarla vacía. Esa decisión NO tiene default a propósito: si el chequeo
 * encontró contactos sin el dato y nadie eligió, el mensaje saldría con un hueco («Hola , …»).
 * La fila se pinta ámbar y el asistente no deja avanzar (CA b, `sinDecision` de logic/mapeo.ts).
 *
 * No resuelve nada: el texto final lo arma el backend con la misma función que el envío.
 *
 * La variable de la imagen que se elige en cada campaña (`imagen`, pedido de Alan del 26/10/2026) no es un dato: se sube
 * una imagen o se elige una de las que ya están, y queda como valor fijo el archivo de la biblioteca.
 */

import { useId } from "react";
import { AlertTriangle } from "lucide-react";
import type { CampoDisponible, CampoVariable, MapeoVariable, MapeoVariables, VariablePlantilla } from "../types";
import { num, plural } from "../../campana-nueva/utils/format";
import { ARCHIVO_IMAGEN_RE, partirLinkDeImagen } from "../logic/imagenes";
import { ElegirImagen } from "./ElegirImagen";

// `campo` es un string: con un público de la base llega un «campo:<clave>» del catálogo de la marca.
type SinResolver = { variable: string; campo: string; contactos: number };

interface Props {
  variables: VariablePlantilla[];
  campos: CampoDisponible[];
  mapeo: MapeoVariables;
  /** Variables que el chequeo marcó sin resolver y todavía sin decisión (`sinDecision`). */
  sinResolver: SinResolver[];
  onChange: (m: MapeoVariables) => void;
  /** Errores locales por variable (`validarMapeoLocal`). */
  errores?: Record<string, string>;
  deshabilitado?: boolean;
  /** La variable de la imagen que se elige en cada campaña y el prefijo de su link (`imagenPorCampana`). */
  imagen?: { numero: number; prefijo: string } | null;
}

/** Un campo personalizado de la base («campo:<clave>»): va agrupado aparte en el selector. */
const esPersonalizado = (campo: string) => campo.startsWith("campo:");

/** Etiqueta de un campo: la del backend (`GET /campos`) o, si no vino, el nombre crudo del campo. */
function etiquetaCampo(campos: CampoDisponible[], campo: string): string {
  return campos.find((c) => c.campo === campo)?.etiqueta || campo;
}

export function MapeoVariablesForm({ variables, campos, mapeo, sinResolver, onChange, errores = {}, deshabilitado = false, imagen = null }: Props) {
  if (!variables?.length) {
    return <p className="text-sm text-slate-500">Esta plantilla no tiene variables: el mensaje es igual para todos.</p>;
  }
  const cambiar = (numero: number, e: MapeoVariable) => onChange({ ...mapeo, [String(numero)]: e });
  return (
    <div className="space-y-4">
      {[...variables].sort((a, b) => a.numero - b.numero).map((v) => (v.numero === imagen?.numero ? (
        <FilaImagen
          key={v.numero}
          prefijo={imagen.prefijo}
          entrada={mapeo[String(v.numero)]}
          error={errores[String(v.numero)]}
          deshabilitado={deshabilitado}
          onCambio={(e) => cambiar(v.numero, e)}
        />
      ) : (
        <FilaVariable
          key={v.numero}
          variable={v}
          campos={campos}
          entrada={mapeo[String(v.numero)]}
          falta={sinResolver.find((s) => s.variable === String(v.numero))}
          error={errores[String(v.numero)]}
          deshabilitado={deshabilitado}
          onCambio={(e) => cambiar(v.numero, e)}
        />
      )))}
    </div>
  );
}

/** La imagen de la campaña: se sube o se elige de la biblioteca, y queda el archivo como valor fijo. */
function FilaImagen({ prefijo, entrada, error, deshabilitado, onCambio }: {
  prefijo: string; entrada: MapeoVariable | undefined; error?: string; deshabilitado: boolean; onCambio: (e: MapeoVariable) => void;
}) {
  const valor = entrada?.fuente === "fijo" ? entrada.valor : "";
  const url = ARCHIVO_IMAGEN_RE.test(valor) ? prefijo + valor : "";
  return (
    <div className="rounded-xl border border-slate-100 p-4 space-y-2" data-testid="variable-imagen">
      <p className="text-sm font-semibold text-slate-800">Imagen de la campaña</p>
      <p className="text-xs text-slate-500">Va arriba del texto. Subí una (JPG o PNG, hasta 5 MB) o elegí una de las que ya están.</p>
      {deshabilitado ? (
        url ? <img src={url} alt="Imagen de la campaña" className="h-24 w-36 object-cover rounded-lg border border-slate-200" /> : <p className="text-sm text-slate-400">Sin imagen elegida.</p>
      ) : (
        <ElegirImagen url={url} onElegir={(u) => {
          const p = partirLinkDeImagen(u);
          if (p) onCambio({ fuente: "fijo", valor: p.archivo });
        }} />
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

function FilaVariable({ variable, campos, entrada, falta, error, deshabilitado, onCambio }: {
  variable: VariablePlantilla; campos: CampoDisponible[]; entrada: MapeoVariable | undefined;
  falta?: SinResolver; error?: string; deshabilitado: boolean; onCambio: (e: MapeoVariable) => void;
}) {
  const id = useId();
  const esCampo = entrada?.fuente === "campo";
  const esFijo = entrada?.fuente === "fijo";
  // Campo por defecto al pasar a «Un dato del cliente»: el sugerido por la plantilla si la audiencia lo
  // tiene (un público de la base no tiene `grupo`), si no el primero que ofrece el backend. Nunca un
  // campo inventado que el backend no conozca.
  const sugeridoDisponible = !!variable.campoSugerido && (!campos.length || campos.some((c) => c.campo === variable.campoSugerido));
  const campoInicial: CampoVariable = (sugeridoDisponible ? variable.campoSugerido : undefined) || campos[0]?.campo || "primerNombre";
  // Si la lista de campos no llegó (o no trae el elegido), igual se muestra el valor actual:
  // un select vacío escondería qué dato se va a usar.
  const opciones = [...campos];
  if (esCampo && !opciones.some((c) => c.campo === entrada.campo)) {
    opciones.push({ campo: entrada.campo, etiqueta: entrada.campo, ejemplo: "" });
  }

  // El nombre de la variable («Nombre», «Oferta»), no `{{1}}`: el número es cómo la guarda Meta, y quien arma la
  // campaña piensa en el dato, no en el hueco.
  const nombreVariable = variable.alias || `Dato ${variable.numero}`;

  return (
    <fieldset
      className={`rounded-xl border p-4 ${falta ? "border-amber-300 bg-amber-50/60" : error ? "border-red-200" : "border-slate-200"}`}
      disabled={deshabilitado}
    >
      <legend className="px-1 text-sm font-semibold text-slate-800">
        <span>{nombreVariable}</span>
        {variable.ejemplo && <span className="ml-2 text-xs font-normal text-slate-400">ej.: {variable.ejemplo}</span>}
      </legend>

      {falta && (
        <p className="mb-3 flex items-start gap-2 text-xs font-medium text-amber-800" role="alert">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
          A {num(falta.contactos)} {plural(falta.contactos, "contacto le falta", "contactos les falta")} «{etiquetaCampo(campos, falta.campo)}». Elegí qué hacer.
        </p>
      )}

      <div className="space-y-3">
        <label className="flex items-start gap-2 text-sm text-slate-700">
          <input
            type="radio"
            name={`${id}-fuente`}
            checked={esCampo}
            onChange={() => onCambio({ fuente: "campo", campo: campoInicial })}
            className="mt-1 accent-violet-600"
          />
          <span className="flex-1">
            Un dato del cliente
            {esCampo && (
              <select
                aria-label={`Dato del cliente para «${nombreVariable}»`}
                value={entrada.campo}
                onChange={(e) => onCambio({ ...entrada, campo: e.target.value as CampoVariable })}
                className="mt-1.5 block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                {opciones.filter((c) => !esPersonalizado(c.campo)).map((c) => (
                  <option key={c.campo} value={c.campo}>{c.etiqueta}{c.ejemplo ? ` (ej.: ${c.ejemplo})` : ""}</option>
                ))}
                {opciones.some((c) => esPersonalizado(c.campo)) && (
                  <optgroup label="Campos de tu base de datos">
                    {opciones.filter((c) => esPersonalizado(c.campo)).map((c) => (
                      <option key={c.campo} value={c.campo}>{c.etiqueta}{c.ejemplo ? ` (ej.: ${c.ejemplo})` : ""}</option>
                    ))}
                  </optgroup>
                )}
              </select>
            )}
          </span>
        </label>

        {esCampo && (
          <div className="ml-6 rounded-lg bg-slate-50 p-3">
            <p className="text-xs font-semibold text-slate-600 mb-2">Si a alguien le falta:</p>
            <div className="space-y-2">
              <label className="flex items-start gap-2 text-sm text-slate-700">
                <input
                  type="radio"
                  name={`${id}-vacio`}
                  checked={entrada.siVacio?.accion === "default"}
                  onChange={() => onCambio({ ...entrada, siVacio: { accion: "default", valor: "" } })}
                  className="mt-1 accent-violet-600"
                />
                <span className="flex-1">
                  Usar este texto:
                  {entrada.siVacio?.accion === "default" && (
                    <input
                      type="text"
                      aria-label={`Texto por defecto para «${nombreVariable}»`}
                      value={entrada.siVacio.valor}
                      maxLength={200}
                      placeholder="¿cómo estás?"
                      onChange={(e) => onCambio({ ...entrada, siVacio: { accion: "default", valor: e.target.value } })}
                      className="mt-1.5 block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                    />
                  )}
                </span>
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="radio"
                  name={`${id}-vacio`}
                  checked={entrada.siVacio?.accion === "excluir"}
                  onChange={() => onCambio({ ...entrada, siVacio: { accion: "excluir" } })}
                  className="accent-violet-600"
                />
                No mandarle
              </label>
              {/* Pedido de Alan del 26/10/2026: «que se puedan poner variables que, si no tiene, también podría estar
                  vacío». Va un carácter invisible (Meta no acepta una variable vacía): lo de alrededor queda. */}
              <label className="flex items-start gap-2 text-sm text-slate-700">
                <input
                  type="radio"
                  name={`${id}-vacio`}
                  checked={entrada.siVacio?.accion === "vacio"}
                  onChange={() => onCambio({ ...entrada, siVacio: { accion: "vacio" } })}
                  className="mt-1 accent-violet-600"
                />
                <span className="flex-1">
                  Dejarla vacía
                  {entrada.siVacio?.accion === "vacio" && (
                    <span className="block text-[11px] text-slate-500 mt-0.5">
                      Le llega igual, sin nada en ese lugar: lo de alrededor queda («Hola , …»). Mirá en «Así les llega» cómo se lee.
                    </span>
                  )}
                </span>
              </label>
            </div>
          </div>
        )}

        <label className="flex items-start gap-2 text-sm text-slate-700">
          <input
            type="radio"
            name={`${id}-fuente`}
            checked={esFijo}
            onChange={() => onCambio({ fuente: "fijo", valor: variable.ejemplo || "" })}
            className="mt-1 accent-violet-600"
          />
          <span className="flex-1">
            Siempre el mismo texto
            {esFijo && (
              <input
                type="text"
                aria-label={`Texto fijo para «${nombreVariable}»`}
                value={entrada.valor}
                maxLength={200}
                onChange={(e) => onCambio({ fuente: "fijo", valor: e.target.value })}
                className="mt-1.5 block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            )}
          </span>
        </label>
      </div>

      {error && <p className="mt-2 text-xs font-medium text-red-600">{error}</p>}
    </fieldset>
  );
}

export default MapeoVariablesForm;
